import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { ConfigProvider, Image, Input, Modal, Popconfirm, Select, Skeleton } from "antd";
import { DownloadOutlined, QrcodeOutlined } from "@ant-design/icons";
import { PDFDownloadLink } from "@react-pdf/renderer";
import dayjs from "dayjs";
import useAxiosClient from "../axios-client.js";
import { useStateContext } from "../contexts/ContextProvider";
import OrderInvoice, { stockPlacementSummary } from "../components/OrderInvoice/OrderInvoice";
import OrderQrLabel from "../components/OrderInvoice/OrderQrLabel";
import OrderQrCode from "../components/OrderQrCode";
import CourierPanel from "../components/Order/CourierPanel";
import { orderDeepLink } from "../utils/orderQr";
import { lineAmount } from "../utils/orderLine";
import { extractOrderNumber } from "../components/Util/OrderNumberFormatter";
import {
  canChangeAnyStatus,
} from "../utils/enums/OrderStatusEnum";
import StatusChangeModal from "../components/Order/StatusChangeModal";
import { fmtBDT, fmtDateTime, hexToRgba, statusMeta, buildStatusSteps, holdReasonLabel } from "../components/Order/orderPrimitives";
import { PRIMARY, SHELL_THEME as THEME, shellAntdTheme, STATUS } from "../shellTokens";

const READY = 5;
const HOLD = 11;
const DELIVERED = 6;
const OUT_FOR_DELIVERY = 10;
const API = process.env.REACT_APP_API_BASE_URL;
// Only an order that has not been produced yet can be filled from ready stock
// (ADR 0003); BOOKING left this list when it moved to after READY.
const ELIGIBLE_STATUSES = [1, 2, 12];
// Statuses from which an accidental stock pull can still be reversed (DRAFT, APPROVED, PRINT, READY, HOLD)
const UNDO_PULL_ELIGIBLE_STATUSES = [1, 2, 12, 5, 11];

function formatAuditChanges(changes) {
  if (!changes || typeof changes !== "object") return [];
  const lines = [];
  if (changes.client_order_number) {
    const o = changes.client_order_number.old || "(none)";
    const n = changes.client_order_number.new || "(none)";
    lines.push(`Client Order #: ${o} → ${n}`);
  }
  if (changes.delivery_date) {
    const o = changes.delivery_date.old ? dayjs(changes.delivery_date.old).format("MMM D, YYYY") : "(none)";
    const n = changes.delivery_date.new ? dayjs(changes.delivery_date.new).format("MMM D, YYYY") : "(none)";
    lines.push(`Delivery Date: ${o} → ${n}`);
  }
  if (changes.delivery_charge) {
    lines.push(`Delivery Charge: ৳${changes.delivery_charge.old} → ৳${changes.delivery_charge.new}`);
  }
  if (changes.amount) {
    lines.push(`Goods Subtotal: ৳${changes.amount.old} → ৳${changes.amount.new}`);
  }
  if (changes.total_amount) {
    lines.push(`Total Amount: ৳${changes.total_amount.old} → ৳${changes.total_amount.new}`);
  }
  if (changes.products) {
    const oldList = changes.products.old || [];
    const newList = changes.products.new || [];
    const oldQty = oldList.reduce((acc, p) => acc + (p.qty || 0), 0);
    const newQty = newList.reduce((acc, p) => acc + (p.qty || 0), 0);
    lines.push(`Products updated (${oldList.length} items, ${oldQty} pcs → ${newList.length} items, ${newQty} pcs)`);
  }
  if (changes.delivery_channel) {
    lines.push(`Delivery channel updated`);
  }
  if (changes.customer_id) {
    lines.push(`Customer updated`);
  }
  if (changes.marketplace_id) {
    lines.push(`Marketplace updated`);
  }
  return lines;
}

const Order = () => {
  const axiosClient = useAxiosClient();
  const { permissions, darkMode, company } = useStateContext();
  const canPull = permissions?.includes("PULL_FROM_STOCK");
  const canReturn = permissions?.includes("RETURN_ORDER");
  const canChangeStatus = canChangeAnyStatus(permissions);

  const [order, setOrder] = useState({});
  const [activeTab, setActiveTab] = useState("items");
  const [returnOpen, setReturnOpen] = useState(false);
  const [returnSelections, setReturnSelections] = useState({});
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [presetStatus, setPresetStatus] = useState(null);
  const [statusSubmitting, setStatusSubmitting] = useState(false);
  const [returnComment, setReturnComment] = useState("");
  const navigate = useNavigate();
  const { id } = useParams();

  const fetchOrders = useCallback(async () => {
    try {
      const res = await axiosClient.get(
        `/orders/getOrder/${extractOrderNumber(id)}`
      );
      setOrder({ ...res.data.data });
    } catch (error) {
      toast.error(error.response?.data?.message || error.message);
      navigate("/notFound");
    }
  }, [axiosClient, id, navigate]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const handlePull = async (item) => {
    try {
      const res = await axiosClient.post(
        `/orders/pullFromStock/${order.id}/${item.id}`
      );
      toast.success(res.data.message);
      fetchOrders();
    } catch (e) {
      toast.error(e.response?.data?.message || e.message);
    }
  };

  const handleUndoPull = async (item) => {
    try {
      const res = await axiosClient.post(
        `/orders/undoPullFromStock/${order.id}/${item.id}`
      );
      toast.success(res.data.message);
      fetchOrders();
    } catch (e) {
      toast.error(e.response?.data?.message || e.message);
    }
  };

  const submitReturns = async () => {
    const lines = Object.entries(returnSelections)
      .filter(([, c]) => c)
      .map(([pid, c]) => ({ productId: Number(pid), condition: c }));
    if (!lines.length) {
      toast.warning(
        "Set at least one product to Sellable or Damaged before recording the return."
      );
      return;
    }
    try {
      const res = await axiosClient.post(`/orders/markReturned/${order.id}`, {
        lines,
        comment: returnComment.trim(),
      });
      toast.success(res.data.message);
      setReturnOpen(false);
      setReturnSelections({});
      setReturnComment("");
      fetchOrders();
    } catch (e) {
      toast.error(e.response?.data?.message || e.message);
    }
  };

  /** `preset` lets Release open the modal with the held-from status chosen. */
  const openStatusModal = (preset = null) => {
    if (!canChangeStatus) return;
    const valid =
      typeof preset === "number" ||
      (typeof preset === "string" && !isNaN(Number(preset)) && preset.trim?.() !== "")
        ? Number(preset)
        : null;
    setPresetStatus(valid);
    setStatusModalOpen(true);
  };

  // One endpoint for every transition (ADR 0006): the reason, the hold category
  // and the per-line restock decisions all travel with it, so a cancel from here
  // is the same transaction as one from the Orders list.
  const confirmStatusChange = async (payload) => {
    setStatusSubmitting(true);
    try {
      const res = await axiosClient.post("/orders/updateOrderStatus", {
        orderId: order.id,
        ...payload,
      });
      toast.success(res.data.message || "Order status updated");
      setStatusModalOpen(false);
      fetchOrders();
      window.dispatchEvent(new CustomEvent('orders:status-changed'));
    } catch (e) {
      toast.error(e.response?.data?.message || e.message);
    } finally {
      setStatusSubmitting(false);
    }
  };

  // Modals portal to <body>, outside the element rootStyle publishes these on,
  // so anything inside one needs the hex rather than var(--x).
  const shell = THEME[darkMode ? "dark" : "light"];

  const rootStyle = {
    ...Object.fromEntries(
      Object.entries(THEME[darkMode ? "dark" : "light"]).map(([k, v]) => [
        `--${k}`,
        v,
      ])
    ),
    "--primary": PRIMARY,
  };

  if (!order.id) {
    return (
      <div style={{ ...rootStyle, padding: 24 }}>
        <Skeleton active paragraph={{ rows: 8 }} />
      </div>
    );
  }

  const statusInfo = statusMeta(order.status, darkMode);
  // A held order's way back: the status it was held from, offered as one click.
  const releaseTo = order.status === HOLD ? order.heldFromStatus : null;
  const isMerchant = !order.customer;
  const products = order.products || [];
  const timeline = [
    ...(order.orderStatusChanges || []).map((t) => ({ ...t, _type: "status" })),
    ...(order.auditLogs || [])
      .filter((a) => a.action === "updated")
      .map((a) => ({ ...a, _type: "audit" })),
  ].sort((a, b) => new Date(b.created_at || b.createdAt) - new Date(a.created_at || a.createdAt));

  // ---- Status stepper ----
  const steps = buildStatusSteps({
    status: order.status,
    heldFromStatus: order.heldFromStatus,
    history: order.orderStatusChanges,
  }).map((step, i) => {
    // The circle is a solid fill with white text on it, so it takes `solid`,
    // not the theme-flipped badge colour.
    const info = statusMeta(step.value);
    return {
      ...step,
      // The exit is not step N of the pipeline — it is where the order left it.
      mark: step.exit
        ? (step.value === HOLD ? "⏸" : "✕")
        : step.done ? "✓" : String(i + 1),
      circleBg: step.done ? STATUS.ok : step.current ? info.solid : "var(--borderStrong)",
      active: step.done || step.current,
    };
  });

  return (
    <div className="et-order-detail" style={rootStyle}>
      <style>{`
        .et-order-detail{font-family:Outfit,system-ui,sans-serif;color:var(--text);height:100%;min-height:0;display:flex;flex-direction:column}
        .et-order-detail ::-webkit-scrollbar{width:7px}
        .et-order-detail ::-webkit-scrollbar-thumb{background:var(--borderStrong);border-radius:5px}
        .et-order-detail ::-webkit-scrollbar-track{background:transparent}
        .od-tab:hover{color:var(--text)}
        .od-btn-ghost:hover{background:var(--hover)}
        .od-btn-ghost-danger:hover{background:rgba(248,113,113,.12);color:${STATUS.error}}
        .od-pull-btn:hover{background:var(--activeBg)}
        @media (max-width: 900px) {
          .et-step-label { font-size: 9.5px !important; max-width: 58px !important; }
          .et-step-node div:first-child { width: 20px !important; height: 20px !important; font-size: 9px !important; }
          .et-step-connector { margin-bottom: 14px !important; min-width: 4px !important; }
        }
        @media (max-width: 640px) {
          .et-stepper-rail { overflow-x: auto !important; -webkit-overflow-scrolling: touch; padding: 10px 12px !important; justify-content: flex-start !important; }
          .et-step-node { min-width: 56px !important; flex-shrink: 0 !important; }
          .et-step-connector { min-width: 14px !important; flex: 0 0 14px !important; }
        }
      `}</style>

      {/* ---- Main card ---- */}
      <div
        style={{
          background: "var(--surface)",
          border: "1px solid var(--border)",
          borderRadius: 14,
          padding: "16px 20px",
          display: "flex",
          flexDirection: "column",
          gap: 16,
          flex: 1,
          minHeight: 0,
        }}
      >
        {/* ---- Header ---- */}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "space-between",
            gap: 14,
            flexShrink: 0,
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <span
                style={{
                  font: "700 20px Outfit, sans-serif",
                  color: "var(--text)",
                }}
              >
                Order #{order.id}
              </span>
              {order.clientOrderNumber && (
                <span
                  style={{
                    font: "500 12.5px var(--mono)",
                    color: "var(--muted)",
                    background: "var(--surface2)",
                    padding: "3px 8px",
                    borderRadius: 6,
                    border: "1px solid var(--border)",
                  }}
                  title="Client Order / PO #"
                >
                  Client #: {order.clientOrderNumber}
                </span>
              )}
              <span
                onClick={() => openStatusModal()}
                style={{
                  font: "600 11px Outfit, sans-serif",
                  color: "#fff",
                  padding: "3px 10px",
                  borderRadius: 10,
                  background: statusInfo.color,
                  cursor: canChangeStatus ? "pointer" : "default",
                }}
              >
                {statusInfo.label}
              </span>
            </div>
            <div style={{ font: "400 13px Outfit, sans-serif", color: "var(--muted)" }}>
              {order.customer?.name ||
                order.marketplace?.name ||
                order.orderable?.name ||
                "—"}{" "}
              · {dayjs(order.createdAt).format("MMM D, YYYY")}
              {order.updatedByName && (
                <span> · Last edited by {order.updatedByName}</span>
              )}
            </div>
          </div>
          <div style={{ display: "flex", gap: 6, alignSelf: "flex-start", flexWrap: "wrap" }}>
            {/* Release from hold — the held-from status, one click away */}
            {canChangeStatus && releaseTo && (
              <button
                type="button"
                onClick={() => openStatusModal(releaseTo)}
                title={`Release to ${statusMeta(releaseTo).label}`}
                style={{ ...iconBtn, width: "auto", padding: "0 12px" }}
              >
                Release to {statusMeta(releaseTo).label}
              </button>
            )}
            {/* Change Status */}
            {canChangeStatus && (
              <button
                type="button"
                onClick={() => openStatusModal()}
                title="Change status"
                style={iconBtn}
              >
                ⇄
              </button>
            )}
            {/* Edit Order */}
            <button
              type="button"
              onClick={() => navigate(`/orders/${order.id}/edit`)}
              title="Edit order"
              style={iconBtn}
            >
              ✎
            </button>
            <PDFDownloadLink
              document={<OrderInvoice order={order} company={company} />}
              fileName={`order-${order.id}-${dayjs().unix()}.pdf`}
            >
              {({ loading }) => (
                <button
                  type="button"
                  disabled={loading}
                  style={{
                    height: 36,
                    padding: "0 14px",
                    borderRadius: 9,
                    border: "1.5px dashed var(--primary)",
                    background: "transparent",
                    color: "var(--primary)",
                    font: "700 12px Outfit, sans-serif",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 7,
                  }}
                >
                  <DownloadOutlined /> Download PDF
                </button>
              )}
            </PDFDownloadLink>
            <PDFDownloadLink
              document={<OrderQrLabel order={order} />}
              fileName={`order-${order.id}-label.pdf`}
            >
              {({ loading }) => (
                <button
                  type="button"
                  disabled={loading}
                  style={{
                    height: 36,
                    padding: "0 14px",
                    borderRadius: 9,
                    border: "1.5px dashed var(--border)",
                    background: "transparent",
                    color: "var(--muted)",
                    font: "700 12px Outfit, sans-serif",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 7,
                  }}
                >
                  <QrcodeOutlined /> QR label
                </button>
              )}
            </PDFDownloadLink>
          </div>
        </div>

        {/* ---- Why this order stopped ---- */}
        {order.hold && (
          <div
            style={{
              display: "flex",
              alignItems: "baseline",
              gap: 8,
              flexWrap: "wrap",
              padding: "10px 14px",
              marginBottom: 12,
              borderRadius: 10,
              border: "1px solid var(--border)",
              borderLeft: `3px solid ${statusInfo.solid}`,
              background: "var(--surface2, transparent)",
              font: "400 12.5px Outfit, sans-serif",
              color: "var(--muted)",
            }}
          >
            <span style={{ font: "700 12.5px Outfit, sans-serif", color: "var(--text)" }}>
              On hold {order.hold.days === 0 ? "since today" : `${order.hold.days} day${order.hold.days === 1 ? "" : "s"}`}
            </span>
            {holdReasonLabel(order.hold.reason) && (
              <span
                style={{
                  font: "700 11px Outfit, sans-serif",
                  color: statusInfo.solid,
                  background: hexToRgba(statusInfo.solid, 0.14),
                  padding: "1px 7px",
                  borderRadius: 8,
                }}
              >
                {holdReasonLabel(order.hold.reason)}
              </span>
            )}
            {/* Where it stopped. Null only for holds placed before the history
                row carried an origin — say nothing rather than guess. */}
            {order.hold.fromStatus != null && (
              <span>from <strong style={{ color: "var(--text)" }}>{statusMeta(order.hold.fromStatus).label}</strong></span>
            )}
            {order.hold.by && <span>· by {order.hold.by}</span>}
            <span>· {fmtDateTime(order.hold.since)}</span>
            {order.hold.comment && (
              <div style={{ flexBasis: "100%", color: "var(--text)", marginTop: 2 }}>
                “{order.hold.comment}”
              </div>
            )}
          </div>
        )}

        {/* ---- Status Stepper (Full Width Pipeline) ---- */}
        <div
          className="et-stepper-rail"
          style={{
            background: "var(--surface2)",
            border: "1px solid var(--border)",
            borderRadius: 12,
            padding: "12px 18px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
            boxSizing: "border-box",
            flexShrink: 0,
            gap: 4,
          }}
        >
          {steps.map((st, i) => (
            <React.Fragment key={st.value}>
              <div
                className={`et-step-node ${st.current ? "is-current" : ""} ${st.done ? "is-done" : ""}`}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  textAlign: "center",
                  gap: 5,
                  position: "relative",
                  zIndex: 1,
                  cursor: canChangeStatus ? "pointer" : "default",
                  minWidth: 0,
                }}
                title={st.label}
                onClick={() => canChangeStatus && !st.exit && openStatusModal(st.value)}
              >
                <div
                  style={{
                    width: st.current ? 26 : 22,
                    height: st.current ? 26 : 22,
                    borderRadius: "50%",
                    background: st.circleBg,
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    font: `700 ${st.current ? "11px" : "10px"} Outfit, sans-serif`,
                    boxShadow: st.current ? `0 0 0 3px ${hexToRgba(st.circleBg, 0.28)}` : "none",
                    transition: "all 0.2s ease",
                    flexShrink: 0,
                  }}
                >
                  {st.mark}
                </div>
                <div
                  className="et-step-label"
                  style={{
                    font: st.current ? "700 11px Outfit, sans-serif" : "600 10.5px Outfit, sans-serif",
                    color: st.current ? "var(--text)" : st.done ? "var(--muted)" : "var(--dim)",
                    lineHeight: 1.25,
                    maxWidth: 78,
                    wordBreak: "break-word",
                    transition: "color 0.2s ease",
                  }}
                >
                  {st.label}
                </div>
              </div>
              {st.hasLine && (
                <div
                  className="et-step-connector"
                  style={{
                    flex: 1,
                    height: 2,
                    minWidth: 8,
                    marginBottom: 18,
                    background: st.lineDone ? STATUS.ok : "var(--border)",
                    transition: "background 0.2s ease",
                  }}
                />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* ---- Two-column body ---- */}
        <div
          style={{
            display: "flex",
            gap: 16,
            alignItems: "stretch",
            flex: 1,
            minHeight: 0,
          }}
          className="max-lg:flex-col"
        >
          {/* ---- Left: tabbed content ---- */}
          <div
            style={{
              flex: 1,
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
            }}
          >
            {/* Tabs */}
            <div
              style={{
                display: "flex",
                gap: 2,
                background: "var(--surface2)",
                borderRadius: 10,
                padding: 3,
                width: "fit-content",
                marginBottom: 12,
                flexShrink: 0,
              }}
            >
              {[
                { k: "items", l: "Items" },
                { k: "timeline", l: "History" },
              ].map((t) => (
                <div
                  key={t.k}
                  className="od-tab"
                  onClick={() => setActiveTab(t.k)}
                  style={{
                    padding: "7px 16px",
                    borderRadius: 8,
                    font: "600 12.5px Outfit, sans-serif",
                    cursor: "pointer",
                    background:
                      activeTab === t.k ? "var(--surface)" : "transparent",
                    color:
                      activeTab === t.k
                        ? "var(--text)"
                        : "var(--muted)",
                    boxShadow:
                      activeTab === t.k
                        ? "0 1px 3px rgba(0,0,0,.12)"
                        : "none",
                    transition: "background .15s",
                  }}
                >
                  {t.l}
                </div>
              ))}
            </div>

            {/* Tab content */}
            <div
              style={{
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                paddingRight: 4,
              }}
            >
              {activeTab === "items" && (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {/* Goods can come back once they are with the courier, not
                      only after delivery — a refused parcel is a return too. */}
                  {canReturn && [DELIVERED, OUT_FOR_DELIVERY].includes(order.status) && (
                    <div style={{ display: "flex", justifyContent: "flex-end" }}>
                      <button
                        type="button"
                        className="od-btn-ghost-danger"
                        onClick={() => setReturnOpen(true)}
                        style={{
                          height: 30,
                          padding: "0 12px",
                          borderRadius: 7,
                          border: "1px solid rgba(248,113,113,.4)",
                          background: "transparent",
                          color: STATUS.error,
                          font: "600 12px Outfit, sans-serif",
                          cursor: "pointer",
                        }}
                      >
                        Mark returned
                      </button>
                    </div>
                  )}

                  {products.map((p) => (
                    <div
                      key={p.id}
                      style={{
                        display: "flex",
                        gap: 12,
                        padding: 12,
                        borderRadius: 12,
                        border: "1px solid var(--border)",
                      }}
                    >
                      <div
                        style={{
                          width: 48,
                          height: 48,
                          borderRadius: 10,
                          overflow: "hidden",
                          flexShrink: 0,
                          background: "var(--surface2)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "var(--muted)",
                          font: "700 16px Outfit, sans-serif",
                        }}
                      >
                        {p.productType?.name?.[0] || "?"}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            gap: 8,
                          }}
                        >
                          <div
                            style={{
                              font: "600 13.5px Outfit, sans-serif",
                              color: "var(--text)",
                            }}
                          >
                            {p.productType?.name}
                          </div>
                          {/* The line's amount, with what makes it up beneath.
                              `price` is the price of one, so a line of six read
                              as the price of one — and did not agree with the
                              order total further down the page. */}
                          <div style={{ textAlign: "right", flexShrink: 0 }}>
                            <div
                              style={{
                                font: "600 13.5px Outfit, sans-serif",
                                color: "var(--text)",
                              }}
                            >
                              {fmtBDT(lineAmount(p))}
                            </div>
                            {p.unit > 1 && (
                              <div
                                style={{
                                  font: "400 11px Outfit, sans-serif",
                                  color: "var(--muted)",
                                  marginTop: 1,
                                }}
                              >
                                {p.unit} × {fmtBDT(p.price)}
                              </div>
                            )}
                          </div>
                        </div>
                        <div
                          style={{
                            font: "600 11.5px Outfit, sans-serif",
                            color: "var(--primary)",
                            marginTop: 1,
                          }}
                        >
                          {[p.fabrics?.name, p.fabricType?.name, p.productFormation?.name]
                            .filter(Boolean)
                            .join(" · ")}
                        </div>
                        {/* The print this line was placed in, by name. */}
                        {p.fabricPattern?.name && (
                          <div
                            style={{
                              font: "600 11.5px Outfit, sans-serif",
                              color: "var(--muted)",
                              marginTop: 5,
                            }}
                          >
                            {p.fabricPattern.name}
                          </div>
                        )}
                        <div
                          style={{
                            font: "400 12px Outfit, sans-serif",
                            color: "var(--muted)",
                            marginTop: 1,
                          }}
                        >
                          {p.description || "No description"} · Qty {p.unit}
                        </div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            marginTop: 6,
                            flexWrap: "wrap",
                          }}
                        >
                          {p.fulfilledFromStock && (
                            <div
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 6,
                              }}
                            >
                              <span style={tagStyle(STATUS.ok)}>
                                Fulfilled from stock{stockPlacementSummary(p) ? ` @ ${stockPlacementSummary(p)}` : ""}
                              </span>
                              {canPull &&
                                UNDO_PULL_ELIGIBLE_STATUSES.includes(order.status) &&
                                !p.isReturned && (
                                  <Popconfirm
                                    title="Undo stock fulfillment?"
                                    description={
                                      order.status === READY
                                        ? `Return ${p.unit} unit(s) to ready stock? Order #${order.id} will be reverted from Ready back to production/print.`
                                        : `Return ${p.unit} unit(s) to ready stock?`
                                    }
                                    onConfirm={() => handleUndoPull(p)}
                                    okText="Yes, undo"
                                    cancelText="Cancel"
                                  >
                                    <button
                                      type="button"
                                      className="od-btn-ghost-danger"
                                      style={{
                                        height: 22,
                                        padding: "0 8px",
                                        borderRadius: 6,
                                        border: "1px solid rgba(248,113,113,0.35)",
                                        background: "transparent",
                                        color: STATUS.error,
                                        font: "600 11px Outfit, sans-serif",
                                        cursor: "pointer",
                                      }}
                                    >
                                      Undo pull
                                    </button>
                                  </Popconfirm>
                                )}
                            </div>
                          )}
                          {p.isReturned && (
                            <span
                              style={tagStyle(
                                p.returnCondition === "SELLABLE"
                                  ? "#f59e0b"
                                  : STATUS.error
                              )}
                            >
                              Returned ({p.returnCondition})
                            </span>
                          )}
                          {!p.fulfilledFromStock &&
                            ELIGIBLE_STATUSES.includes(order.status) &&
                            p.availableInStock > 0 && (
                              <>
                                <span style={tagStyle(STATUS.ok)}>
                                  {p.availableInStock} in ready stock{stockPlacementSummary(p) ? ` @ ${stockPlacementSummary(p)}` : ""}
                                </span>
                                {canPull && (
                                  <Popconfirm
                                    title="Pull from ready stock?"
                                    description={`Fulfill ${p.unit} unit(s) from ready stock for this order?`}
                                    onConfirm={() => handlePull(p)}
                                    okText="Yes, pull"
                                    cancelText="Cancel"
                                  >
                                    <button
                                      type="button"
                                      className="od-pull-btn"
                                      style={{
                                        height: 26,
                                        padding: "0 10px",
                                        borderRadius: 7,
                                        border: "none",
                                        background: "transparent",
                                        color: "var(--primary)",
                                        font: "600 12px Outfit, sans-serif",
                                        cursor: "pointer",
                                      }}
                                    >
                                      Pull from stock
                                    </button>
                                  </Popconfirm>
                                )}
                              </>
                            )}
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* Attachments */}
                  <div style={{ marginTop: 8 }}>
                    <div
                      style={{
                        font: "600 11px Outfit, sans-serif",
                        color: "var(--dim)",
                        textTransform: "uppercase",
                        letterSpacing: ".05em",
                        marginBottom: 6,
                      }}
                    >
                      Attachments
                    </div>
                    {order.images && order.images.length ? (
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                        {order.images.map((img) => (
                          <Image
                            key={img.id}
                            width={132}
                            height={132}
                            src={`${API}/files/upload/${img.id}`}
                            style={{
                              objectFit: "cover",
                              borderRadius: 10,
                              border: "1px solid var(--border)",
                            }}
                            preview={{ mask: "View" }}
                          />
                        ))}
                      </div>
                    ) : (
                      <div
                        style={{
                          font: "400 12.5px Outfit, sans-serif",
                          color: "var(--dim)",
                        }}
                      >
                        No attachments.
                      </div>
                    )}
                  </div>
                </div>
              )}

              {activeTab === "timeline" && (
                <div style={{ display: "flex", flexDirection: "column" }}>
                  {timeline.length ? (
                    timeline.map((t, i) => {
                      if (t._type === "audit") {
                        const changeLines = formatAuditChanges(t.changes);
                        return (
                          <div
                            key={`audit-${t.id || i}`}
                            style={{
                              display: "flex",
                              gap: 10,
                              padding: "10px 0",
                              borderBottom: "1px solid var(--border)",
                            }}
                          >
                            <div
                              style={{
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                marginTop: 5,
                                flexShrink: 0,
                                background: "#8b5cf6",
                              }}
                            />
                            <div>
                              <div
                                style={{
                                  display: "flex",
                                  gap: 8,
                                  alignItems: "center",
                                  flexWrap: "wrap",
                                }}
                              >
                                <span
                                  style={{
                                    font: "600 11px Outfit, sans-serif",
                                    color: "#fff",
                                    padding: "2px 8px",
                                    borderRadius: 8,
                                    background: "#8b5cf6",
                                  }}
                                >
                                  ✎ Order Edited
                                </span>
                                <span
                                  style={{
                                    font: "400 11px Outfit, sans-serif",
                                    color: "var(--dim)",
                                  }}
                                >
                                  {dayjs(t.createdAt || t.created_at).format(
                                    "MMM D, YYYY h:mm A"
                                  )}
                                </span>
                              </div>
                              {changeLines.length > 0 && (
                                <div
                                  style={{
                                    font: "400 12px Outfit, sans-serif",
                                    color: "var(--text)",
                                    marginTop: 4,
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 2,
                                  }}
                                >
                                  {changeLines.map((line, idx) => (
                                    <div key={idx}>• {line}</div>
                                  ))}
                                </div>
                              )}
                              {t.user && (
                                <div
                                  style={{
                                    font: "400 11px Outfit, sans-serif",
                                    color: "var(--dim)",
                                    fontStyle: "italic",
                                    marginTop: 2,
                                  }}
                                >
                                  by {t.user.firstname} {t.user.lastname || ""}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      }

                      const info = statusMeta(t.status, darkMode);
                      return (
                        <div
                          key={i}
                          style={{
                            display: "flex",
                            gap: 10,
                            padding: "10px 0",
                            borderBottom: "1px solid var(--border)",
                          }}
                        >
                          <div
                            style={{
                              width: 8,
                              height: 8,
                              borderRadius: "50%",
                              marginTop: 5,
                              flexShrink: 0,
                              background: info.color || "var(--dim)",
                            }}
                          />
                          <div>
                            <div
                              style={{
                                display: "flex",
                                gap: 8,
                                alignItems: "center",
                                flexWrap: "wrap",
                              }}
                            >
                              {/* Where the order came from, when the row knows.
                                  Null on an order's first entry and on rows
                                  written before from_status existed. */}
                              {t.from_status != null && (
                                <span
                                  style={{
                                    font: "400 11px Outfit, sans-serif",
                                    color: "var(--dim)",
                                  }}
                                >
                                  {statusMeta(t.from_status).label} →
                                </span>
                              )}
                              <span
                                style={{
                                  font: "600 11px Outfit, sans-serif",
                                  color: "#fff",
                                  padding: "2px 8px",
                                  borderRadius: 8,
                                  background: info.color || "var(--dim)",
                                }}
                              >
                                {info.label}
                              </span>
                              {holdReasonLabel(t.hold_reason) && (
                                <span
                                  style={{
                                    font: "700 10.5px Outfit, sans-serif",
                                    color: info.color || "var(--dim)",
                                    background: hexToRgba(info.color, 0.14),
                                    padding: "1px 6px",
                                    borderRadius: 7,
                                  }}
                                >
                                  {holdReasonLabel(t.hold_reason)}
                                </span>
                              )}
                              <span
                                style={{
                                  font: "400 11px Outfit, sans-serif",
                                  color: "var(--dim)",
                                }}
                              >
                                {dayjs(t.created_at).format(
                                  "MMM D, YYYY h:mm A"
                                )}
                              </span>
                            </div>
                            {t.comment && (
                              <div
                                style={{
                                  font: "400 12.5px Outfit, sans-serif",
                                  color: "var(--text)",
                                  marginTop: 4,
                                }}
                              >
                                {t.comment}
                              </div>
                            )}
                            {t.user && (
                              <div
                                style={{
                                  font: "400 11px Outfit, sans-serif",
                                  color: "var(--dim)",
                                  fontStyle: "italic",
                                  marginTop: 2,
                                }}
                              >
                                by {t.user.firstname}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div
                      style={{
                        font: "400 12.5px Outfit, sans-serif",
                        color: "var(--dim)",
                        padding: "16px 0",
                      }}
                    >
                      No history yet.
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* ---- Right: summary panel ---- */}
          <div
            style={{
              width: 280,
              flexShrink: 0,
              background: "var(--surface2)",
              borderRadius: 14,
              padding: 14,
              display: "flex",
              flexDirection: "column",
              gap: 12,
              overflowY: "auto",
            }}
            className="max-lg:w-full"
          >
            {/* Customer / Merchant */}
            <div>
              <div style={panelLabel}>
                {isMerchant ? "Merchant" : "Customer"}
              </div>
              {isMerchant ? (
                <>
                  <div style={panelValue}>
                    {order.orderable?.name}
                  </div>
                  {order.merchantRef && (
                    <div
                      style={{
                        font: "400 12px Outfit, sans-serif",
                        color: "var(--muted)",
                        marginTop: 2,
                      }}
                    >
                      Ref: {order.merchantRef}
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div style={panelValue}>
                    {order.customer?.name}
                  </div>
                  <div
                    style={{
                      font: "400 12px Outfit, sans-serif",
                      color: "var(--muted)",
                      marginTop: 2,
                    }}
                  >
                    {[
                      order.customer?.address?.address,
                      order.customer?.address?.upazila?.name,
                      order.customer?.address?.district?.name,
                    ]
                      .filter(Boolean)
                      .join(", ")}
                  </div>
                  <div
                    style={{
                      font: "400 12px Outfit, sans-serif",
                      color: "var(--muted)",
                    }}
                  >
                    {[order.customer?.address?.phone, order.customer?.altPhone]
                      .filter(Boolean)
                      .join(" · ")}
                  </div>
                  {order.customer?.facebook && (
                    <a
                      href={
                        /^https?:\/\//.test(order.customer.facebook)
                          ? order.customer.facebook
                          : `https://facebook.com/${order.customer.facebook}`
                      }
                      target="_blank"
                      rel="noreferrer"
                      style={{
                        display: "block",
                        font: "400 12px Outfit, sans-serif",
                        color: "var(--primary)",
                        marginTop: 2,
                        wordBreak: "break-all",
                      }}
                    >
                      {order.customer.facebook}
                    </a>
                  )}
                </>
              )}
            </div>

            <div style={{ height: 1, background: "var(--border)" }} />

            {/* Delivery */}
            <div>
              <div style={panelLabel}>Delivery</div>
              <div style={panelRow}>
                <span>Channel</span>
                <span style={{ fontWeight: 600 }}>
                  {order.deliveryChannel?.name || "—"}
                </span>
              </div>
              <div style={{ ...panelRow, marginTop: 4 }}>
                <span>Date</span>
                <span style={{ fontWeight: 600 }}>
                  {dayjs(order.deliveryDate).format("MMM D, YYYY")}
                </span>
              </div>
            </div>

            {/* Renders nothing unless the order was booked over a courier API. */}
            <CourierPanel consignment={order.consignment} />

            <div style={{ height: 1, background: "var(--border)" }} />

            {/* Payment */}
            <div>
              <div style={panelLabel}>Payment</div>
              <div style={panelRow}>
                <span>Subtotal</span>
                <span>{fmtBDT(order.payment?.amount)}</span>
              </div>
              <div style={{ ...panelRow, marginTop: 4 }}>
                <span>Delivery</span>
                <span>{fmtBDT(order.payment?.deliveryCharge)}</span>
              </div>
              <div
                style={{
                  height: 1,
                  background: "var(--border)",
                  margin: "6px 0",
                }}
              />
              <div style={{ ...panelRow, fontWeight: 700, fontSize: 15 }}>
                <span>Total</span>
                <span>{fmtBDT(order.payment?.totalAmount)}</span>
              </div>
            </div>

            <div style={{ height: 1, background: "var(--border)" }} />

            {/* QR */}
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <div style={{ ...panelLabel, alignSelf: "flex-start" }}>
                Order QR
              </div>
              <div
                style={{
                  padding: 8,
                  background: "var(--surface)",
                  borderRadius: 12,
                  border: "1px solid var(--border)",
                }}
              >
                <OrderQrCode value={orderDeepLink(order.id)} size={104} />
              </div>
              <div
                style={{
                  font: "400 10.5px Outfit, sans-serif",
                  color: "var(--dim)",
                  marginTop: 6,
                  textAlign: "center",
                }}
              >
                Scan to open · advances status
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ---- Responsive helper ---- */}
      <style>{`
        @media (max-width: 1024px) {
          .max-lg\\:flex-col { flex-direction: column !important; }
          .max-lg\\:w-full { width: 100% !important; }
        }
      `}</style>

      <ConfigProvider theme={shellAntdTheme(darkMode)}>
        {/* ---- Status modal (shared with the Orders list) ---- */}
        <StatusChangeModal
          open={statusModalOpen}
          order={order}
          permissions={permissions}
          initialStatus={presetStatus}
          submitting={statusSubmitting}
          onCancel={() => setStatusModalOpen(false)}
          onSubmit={confirmStatusChange}
        />

        {/* ---- Returns modal ---- */}
        <Modal
          title="Mark lines as returned"
          open={returnOpen}
          onOk={submitReturns}
          onCancel={() => setReturnOpen(false)}
          okText="Record return"
          okButtonProps={{ danger: true, disabled: !returnComment.trim() }}
        >
          <div
            style={{
              font: "400 12.5px Outfit, sans-serif",
              color: shell.muted,
            }}
          >
            Set each returned line to <b>Sellable</b> or <b>Damaged</b> (leave
            others as "Not returned"). Sellable lines are added to ready stock.
          </div>
          {/* A return is an exit: it needs a reason, same as a cancellation. */}
          <Input.TextArea
            rows={2}
            style={{ marginTop: 12 }}
            placeholder="Why is it coming back? (required)"
            value={returnComment}
            onChange={(e) => setReturnComment(e.target.value)}
            maxLength={200}
            showCount
          />
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 12,
              marginTop: 12,
            }}
          >
            {products.length === 0 && (
              <div
                style={{
                  font: "400 12.5px Outfit, sans-serif",
                  color: shell.dim,
                }}
              >
                This order has no products.
              </div>
            )}
            {products.map((p) => (
              <div
                key={p.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                }}
              >
                <span style={{ font: "400 12.5px Outfit, sans-serif" }}>
                  {p.productType?.name} · {p.fabrics?.name} · Qty {p.unit}
                </span>
                {p.isReturned ? (
                  <span
                    style={tagStyle(
                      p.returnCondition === "SELLABLE" ? "#f59e0b" : STATUS.error
                    )}
                  >
                    Returned ({p.returnCondition})
                  </span>
                ) : (
                  <Select
                    allowClear
                    style={{ width: 150 }}
                    placeholder="Not returned"
                    value={returnSelections[p.id]}
                    onChange={(v) =>
                      setReturnSelections((prev) => ({
                        ...prev,
                        [p.id]: v,
                      }))
                    }
                    options={[
                      { value: "SELLABLE", label: "Sellable" },
                      { value: "DAMAGED", label: "Damaged" },
                    ]}
                  />
                )}
              </div>
            ))}
          </div>
        </Modal>
      </ConfigProvider>
    </div>
  );
};

/* ---- Shared styles ---- */
const panelLabel = {
  font: "600 11px Outfit, sans-serif",
  color: "var(--dim)",
  textTransform: "uppercase",
  letterSpacing: ".05em",
  marginBottom: 4,
};

const panelValue = {
  font: "600 13.5px Outfit, sans-serif",
  color: "var(--text)",
};

const panelRow = {
  display: "flex",
  justifyContent: "space-between",
  font: "400 12.5px Outfit, sans-serif",
  color: "var(--text)",
};

const iconBtn = {
  width: 36,
  height: 36,
  borderRadius: 9,
  border: "1px solid var(--border)",
  background: "var(--surface2)",
  color: "var(--muted)",
  fontSize: 14,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  padding: 0,
};

const tagStyle = (color) => ({
  display: "inline-flex",
  font: "600 10.5px Outfit, sans-serif",
  color,
  background: `${color}22`,
  padding: "2px 8px",
  borderRadius: 6,
});

export default Order;
